// CategoryShowcase.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { fetchAfterHero, registerShownProducts } from '../utils/feedRegistry';
import '../StylesComponents/category-showcase.css';

const TILES = [
  { label: 'MEN', params: { gender: 'men' } },
  { label: 'WOMEN', params: { gender: 'women' } },
  { label: 'SHOES', params: { categories: 'Shoes' } },
  { label: 'STREETWEAR', params: { categories: 'Hoodies,Sweaters,Jeans' } },
  { label: 'ACTIVEWEAR', params: { categories: 'Activewear' } },
  { label: 'LUXURY', params: { priceRanges: '300+' } },
];

/** Names that visually read as gym / training kit for the Activewear tile. */
const ACTIVEWEAR_LOOK_RX = /\b(legging|tights?|sports?\s*bra|bike\s*shorts?|gym|workout|training|yoga|compression|scrunch|running\s*(shorts?|tights?)|performance|activewear|track\s*pants?|joggers?|sports?\s*tops?|seamless|rash\s*guard|dry[\s-]?fit)\b/i;
const ACTIVEWEAR_EXCLUDE_RX = /\b(sneaker|boot|loafer|heel|handbag|wallet|dress\b|skirt\b|blazer|trench|parka|puffer|coat|jean\b|denim|kids?\b|infant|toddler|baby\b|fleece\s*legging)\b/i;

function looksLikeActivewear(product) {
  const name = String(product?.name || '');
  if (String(product?.gender || '').toLowerCase() === 'kids') return false;
  if (ACTIVEWEAR_EXCLUDE_RX.test(name)) return false;
  return ACTIVEWEAR_LOOK_RX.test(name);
}

// Tile images are chosen server-side from each filter's quality pool and rotate
// once per calendar day (Australia/Sydney).
function buildQueryString(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) search.set(key === 'categories' ? 'category' : key, value);
  });
  return search.toString();
}

function CategoryShowcase() {
  const navigate = useNavigate();
  const [tiles, setTiles] = useState(TILES.map(t => ({ ...t, image: null, maxDiscount: 0 })));

  useEffect(() => {
    let cancelled = false;

    const fetchTileData = async () => {
      // One chain slot for all tiles: they share the same exclusion snapshot,
      // fetch 2 candidates each, then claim unique picks in tile order.
      const results = await fetchAfterHero((exclude) => {
        const excludeParam = exclude.length > 0 ? { exclude: exclude.join(',') } : {};
        return Promise.all(
          TILES.map(async (tile) => {
            try {
              const res = await api.get('/api/products', {
                params: {
                  ...tile.params,
                  limit: tile.label === 'ACTIVEWEAR' ? 8 : 3,
                  feedContext: 'category',
                  discountRanges: '10+',
                  ...excludeParam,
                },
              });
              return { ...tile, candidates: res.data.products || [] };
            } catch (e) {
              console.error(`Category tile fetch error (${tile.label}):`, e);
              return { ...tile, candidates: [] };
            }
          })
        );
      });

      // Claim by groupKey — the same product can appear under multiple
      // retailer offers (different _ids), so _id alone doesn't dedupe tiles.
      const claimKey = (p) => String(p.groupKey || p._id || p.id || '');
      const claimed = new Set();
      const tilesWithPicks = results.map((tile) => {
        const candidates = tile.label === 'ACTIVEWEAR'
          ? tile.candidates.filter(looksLikeActivewear)
          : tile.candidates;
        const pick = candidates.find((p) => !claimed.has(claimKey(p)));
        if (!pick) return { ...tile, image: null, maxDiscount: 0 };
        claimed.add(claimKey(pick));
        registerShownProducts([pick]);
        return {
          ...tile,
          image: pick.images?.[0] || pick.image || null,
          maxDiscount: pick.discountPercentage || pick.highestMarketDiscount || 0,
        };
      });

      if (!cancelled) setTiles(tilesWithPicks);
    };

    fetchTileData();
    return () => { cancelled = true; };
  }, []);

  const handleTileClick = (tile) => {
    navigate(`/all-brands?${buildQueryString(tile.params)}`);
  };

  return (
    <section className="category-showcase" aria-label="Shop by category">
      <div className="category-showcase-header">
        <div className="category-showcase-eyebrow">START SHOPPING</div>
        <h2 className="category-showcase-heading">SHOP BY CATEGORY</h2>
      </div>

      <div className="category-showcase-grid">
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            className="category-tile"
            onClick={() => handleTileClick(tile)}
            aria-label={`Shop ${tile.label}`}
          >
            <div className="category-tile-image-wrap">
              {tile.image ? (
                <img src={tile.image} alt={tile.label} className="category-tile-image" loading="lazy" />
              ) : (
                <div className="category-tile-image category-tile-image--placeholder" />
              )}
            </div>
            <div className="category-tile-overlay">
              <h3 className="category-tile-label">{tile.label}</h3>
              {tile.maxDiscount > 0 && (
                <span className="category-tile-discount">UP TO {Math.round(tile.maxDiscount)}% OFF</span>
              )}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

export default CategoryShowcase;
